import { useCallback } from "react";
import { useAppInsights } from "../services/appInsights";
import { TelemetryEvents } from "./eventNames";
import { TelemetryProp, toTelemetryProps } from "./telemetryProps";
import { TelemetrySurface, TelemetrySurfaceValue } from "./surfaces";

type BaseProps = Record<string, string | undefined | null>;

export function useTaskpaneTelemetry() {
  const { trackEvent } = useAppInsights();

  const emit = useCallback(
    (name: string, surface: TelemetrySurfaceValue, props?: BaseProps) => {
      trackEvent({
        name,
        properties: toTelemetryProps({
          [TelemetryProp.surface]: surface,
          ...props,
        }),
      });
    },
    [trackEvent]
  );

  return {
    trackReceivableChaseSucceeded: (props: BaseProps) =>
      emit(TelemetryEvents.receivableChaseSucceeded, TelemetrySurface.receivablesTable, props),
    trackReceivableChaseFailed: (props: BaseProps) =>
      emit(TelemetryEvents.receivableChaseFailed, TelemetrySurface.receivablesTable, props),
    trackReceivableCompleteSucceeded: (props: BaseProps) =>
      emit(TelemetryEvents.receivableCompleteSucceeded, TelemetrySurface.receivablesTable, props),
    trackReceivableCompleteFailed: (props: BaseProps) =>
      emit(TelemetryEvents.receivableCompleteFailed, TelemetrySurface.receivablesTable, props),
    trackReceivableSkipApplied: (props: BaseProps) =>
      emit(TelemetryEvents.receivableSkipApplied, TelemetrySurface.receivablesTable, props),
    trackReceivableSkipFailed: (props: BaseProps) =>
      emit(TelemetryEvents.receivableSkipFailed, TelemetrySurface.receivablesTable, props),

    trackReceivableSearchQuerySubmitted: (props: BaseProps) =>
      emit(TelemetryEvents.searchQuerySubmitted, TelemetrySurface.searchReceivables, props),
    trackReceivableSearchResultSelected: (props: BaseProps) =>
      emit(TelemetryEvents.searchResultSelected, TelemetrySurface.searchReceivables, props),
    trackPayableSearchQuerySubmitted: (props: BaseProps) =>
      emit(TelemetryEvents.payableSearchQuerySubmitted, TelemetrySurface.searchPayables, props),
    trackPayableSearchResultSelected: (props: BaseProps) =>
      emit(TelemetryEvents.payableSearchResultSelected, TelemetrySurface.searchPayables, props),

    trackEmailAssignSucceeded: (props: BaseProps) =>
      emit(TelemetryEvents.emailAssignSucceeded, TelemetrySurface.emailAssignment, props),
    trackEmailAssignFailed: (props: BaseProps) =>
      emit(TelemetryEvents.emailAssignFailed, TelemetrySurface.emailAssignment, props),

    trackActivityCommentSubmitted: (props: BaseProps) =>
      emit(TelemetryEvents.activityCommentSubmitted, TelemetrySurface.activities, props),
    trackActivityCommentFailed: (props: BaseProps) =>
      emit(TelemetryEvents.activityCommentFailed, TelemetrySurface.activities, props),

    trackNavigationDashboardEntryClicked: (props: BaseProps) =>
      emit(TelemetryEvents.navigationDashboardEntryClicked, TelemetrySurface.dashboard, props),
    trackNavigationReceivablesHubAction: (props: BaseProps) =>
      emit(TelemetryEvents.navigationReceivablesHubAction, TelemetrySurface.receivablesHub, props),

    trackPayableSupplierListViewed: (props: BaseProps) =>
      emit(TelemetryEvents.payableSupplierListViewed, TelemetrySurface.payableList, props),
    trackPayableInvoiceRowOpened: () => emit(TelemetryEvents.payableInvoiceRowOpened, TelemetrySurface.payableList),

    trackReceivableGroupedChaseViewed: (props: BaseProps) =>
      emit(TelemetryEvents.receivableGroupedChaseViewed, TelemetrySurface.groupedChase, props),
    trackReceivableToneAdjusted: (props: BaseProps) =>
      emit(TelemetryEvents.receivableToneAdjusted, TelemetrySurface.receivablesTable, props),
    trackReceivableOrganisationAttachmentAdded: (props: BaseProps) =>
      emit(TelemetryEvents.receivableOrganisationAttachmentAdded, TelemetrySurface.receivablesTable, props),
  };
}
